import { useAccount } from "@qw/auth";
import {
  type InboxConversationsListConversationFragment,
  useInboxConversationsQuery,
  useInboxConversationUpdatedSubscription,
  useInboxMessageCreatedSubscription,
  useInboxMessageReadySubscription,
} from "@qw/graphql";
import { usePagination } from "@qw/ui";
import { useCallback, useEffect, useRef } from "react";
import { useInboxFilter } from "./inbox-filter-provider";

export const useConversations = (): Omit<ReturnType<typeof useInboxConversationsQuery>, "data"> & {
  conversations: InboxConversationsListConversationFragment[];
} => {
  const {
    filters,
    search: { onCompleted: onSearchCompleted, onError: onSearchError },
  } = useInboxFilter();
  const { setNumRecords } = usePagination();
  const { userInbox } = useAccount();
  const inboxEmail = userInbox?.email ?? "";
  const { data, ...restConversationsQuery } = useInboxConversationsQuery({
    variables: { input: filters },
    onCompleted: (data) => {
      const resultCount = data.inboxConversations.numRecords;
      setNumRecords(resultCount);
      onSearchCompleted(resultCount);
    },
    onError: onSearchError,
    fetchPolicy: "cache-and-network",
  });
  const conversationUpdateRefetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const conversationUpdateRefetchPendingRef = useRef(false);

  const scheduleConversationUpdateRefetch = useCallback(() => {
    conversationUpdateRefetchPendingRef.current = true;

    if (conversationUpdateRefetchTimeoutRef.current) {
      return;
    }

    conversationUpdateRefetchTimeoutRef.current = setTimeout(() => {
      conversationUpdateRefetchTimeoutRef.current = null;
      if (!conversationUpdateRefetchPendingRef.current) {
        return;
      }

      conversationUpdateRefetchPendingRef.current = false;
      void restConversationsQuery.refetch();
    }, 250);
  }, [restConversationsQuery.refetch]);

  useEffect(() => {
    return () => {
      if (conversationUpdateRefetchTimeoutRef.current) {
        clearTimeout(conversationUpdateRefetchTimeoutRef.current);
      }
      conversationUpdateRefetchPendingRef.current = false;
    };
  }, []);

  useInboxMessageCreatedSubscription({
    variables: { email: inboxEmail },
    onData: () => restConversationsQuery.refetch(),
    skip: !inboxEmail,
  });

  useInboxMessageReadySubscription({
    variables: { email: inboxEmail },
    onData: () => restConversationsQuery.refetch(),
    skip: !inboxEmail,
  });

  useInboxConversationUpdatedSubscription({
    variables: { email: inboxEmail },
    onData: scheduleConversationUpdateRefetch,
    skip: !inboxEmail,
  });

  return { conversations: data?.inboxConversations.conversations || [], ...restConversationsQuery };
};
